// src/pages/FeaturesPage.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Calculate as CalculateIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  CloudDone as CloudDoneIcon,
  Security as SecurityIcon,
  Folder as FolderIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Speed as SpeedIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

const FeaturesPage = () => {
  const navigate = useNavigate();

  const mainFeatures = [
    {
      icon: <ReceiptIcon sx={{ fontSize: 50 }} />,
      title: 'Intelligente Buchhaltung',
      description: 'Automatische Buchungserstellung aus Belegen mit KI-gestützter Texterkennung',
      details: [
        'SKR-kompatible Kontenrahmen (SKR03/SKR04)',
        'Automatische Kategorisierung von Belegen',
        'Steuermodule (Anlage V, V+V, USt)',
        'Export für DATEV und Steuerberater',
        'Umsatzsteuer-Voranmeldung'
      ]
    },
    {
      icon: <CalculateIcon sx={{ fontSize: 50 }} />,
      title: 'Nebenkostenabrechnung',
      description: 'Rechtskonforme NK-Abrechnung in 4 einfachen Schritten',
      details: [
        'Vollautomatische Berechnung nach Verteilerschlüssel',
        'Heizkostenverteiler-Support (Selbstableser)',
        'Daten-Check prüft auf Plausibilität',
        'Versandfertiges PDF mit einem Klick',
        'Zählerstandserfassung digital'
      ]
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 50 }} />,
      title: 'Vertragsmanagement',
      description: 'Alle Verträge digital an einem Ort',
      details: [
        'Mietverträge, Darlehensverträge, Versicherungen',
        'Kündigungsfristen-Tracking',
        'Automatische ToDo-Erinnerungen',
        'Über 30 rechtskonforme Vorlagen',
        'Digitale Unterschrift möglich'
      ]
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 50 }} />,
      title: 'Forderungsmanagement',
      description: 'Wiederkehrende Forderungen automatisieren',
      details: [
        'Automatische Mietzahlungs-Kontrolle',
        'Mahnwesen mit mehrstufigem Prozess',
        'Zahlungspartner-Verwaltung',
        'Offene Posten Dashboard',
        'Export für Inkasso'
      ]
    },
    {
      icon: <FolderIcon sx={{ fontSize: 50 }} />,
      title: 'Dokumentenmanagement',
      description: 'Digitales Archiv für alle Ihre Unterlagen',
      details: [
        'Cloudinary-Integration für sichere Speicherung',
        'Drag & Drop Upload',
        'OCR-Texterkennung für Belege',
        'Kategorisierung und Tags',
        'Volltextsuche über alle Dokumente'
      ]
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 50 }} />,
      title: 'Live-Dashboard & Reporting',
      description: 'Alle wichtigen Kennzahlen auf einen Blick',
      details: [
        'Echtzeit-Cashflow-Analyse',
        'Objektauslastung und Leerstand',
        'Mieteinnahmen-Prognosen',
        'Steuervorauszahlungen-Tracker',
        'Custom Reports & Excel-Export'
      ]
    }
  ];

  const additionalFeatures = [
    {
      category: 'Finanzen',
      items: [
        'Banking-Integration (Kontobewegungen importieren)',
        'Mehrwährungsfähigkeit',
        'Budgetplanung und -kontrolle',
        'Liquiditätsvorschau'
      ]
    },
    {
      category: 'Steuern',
      items: [
        'Anlage V Vorbereitung',
        'Anlage V+V für gewerbliche Vermietung',
        'AfA-Verwaltung (Abschreibungen)',
        'NK-Umsatzsteuer-Prognose'
      ]
    },
    {
      category: 'Kommunikation',
      items: [
        'Mieter-Portal (geplant)',
        'E-Mail-Benachrichtigungen',
        'Mahnungen und Erinnerungen',
        'Dokumentenversand'
      ]
    },
    {
      category: 'Verwaltung',
      items: [
        'Multi-User-Fähigkeit',
        'Rollen und Berechtigungen',
        'Objekt-übergreifende Auswertungen',
        'Mietanpassungen (Indexmiete, Staffelmiete)'
      ]
    }
  ];

  const workflow = [
    {
      number: '1',
      title: 'Daten eingeben',
      description: 'Beginnen Sie, indem Sie alle Informationen zu Ihren Immobilien, Mietern und Verträgen eingeben.'
    },
    {
      number: '2',
      title: 'Kosten erfassen',
      description: 'Erfassen Sie alle Ausgaben und Einnahmen. Die Zuordnung ist einfach und die Kategorisierung erfolgt automatisch.'
    },
    {
      number: '3',
      title: 'Überprüfen',
      description: 'Bevor Sie Reports erstellen, können Sie alle Daten überprüfen und korrigieren.'
    },
    {
      number: '4',
      title: 'Fertig!',
      description: 'Sie können Nebenkostenabrechnungen, Steuerberichte und mehr mit einem Klick erstellen und versenden.'
    }
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: '#1a365d',
          borderBottom: 'none'
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          >
            <AccountBalanceIcon sx={{ color: '#00bcd4' }} /> Immo-Automation
          </Typography>
          <Button 
            sx={{ color: 'white', mr: 2, textTransform: 'none' }}
            onClick={() => navigate('/features')}
          >
            Funktionen
          </Button>
          <Button 
            sx={{ color: 'white', mr: 2, textTransform: 'none' }}
            onClick={() => navigate('/preise')}
          >
            Preise
          </Button>
          <Button 
            sx={{ color: 'white', mr: 2, textTransform: 'none' }}
            onClick={() => navigate('/kontakt')}
          >
            Hilfe & Kontakt
          </Button>
          <Button 
            variant="outlined"
            onClick={() => navigate('/login')}
            sx={{ 
              borderColor: 'white',
              color: 'white',
              borderRadius: 8,
              px: 3,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#00bcd4',
                bgcolor: alpha('#00bcd4', 0.1)
              }
            }}
          >
            Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f2942 0%, #1a365d 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 3 }}>
            Alle Funktionen im Überblick
          </Typography>
          <Typography variant="h5" sx={{ mb: 5, opacity: 0.9, fontWeight: 400 }}>
            Eine Software für die gesamte Immobilienverwaltung – von Buchhaltung 
            bis Nebenkostenabrechnung
          </Typography>
          <Button 
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{ 
              bgcolor: '#00bcd4',
              color: 'white',
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              borderRadius: 8,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#00acc1'
              }
            }}
          >
            Jetzt kostenlos testen
          </Button>
        </Container>
      </Box>

      {/* Workflow Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
            In 4 einfachen Schritten zur Nebenkostenabrechnung
          </Typography>
          <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 400 }}>
            Entwickelt gemeinsam mit privaten Vermietern und Hausverwaltungen
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {workflow.map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  border: '2px solid #e2e8f0',
                  borderRadius: 3,
                  textAlign: 'center',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    borderColor: '#00bcd4'
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#00bcd4',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2.5rem',
                      fontWeight: 700,
                      mx: 'auto',
                      mb: 3
                    }}
                  >
                    {step.number}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                    {step.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Main Features Section */}
      <Box sx={{ bgcolor: 'white', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
              Hauptfunktionen
            </Typography>
            <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 400 }}>
              Professionelle Tools für Ihre Immobilienverwaltung
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {mainFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    border: '2px solid #e2e8f0',
                    borderRadius: 3,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      borderColor: '#00bcd4'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box 
                      sx={{ 
                        color: '#00bcd4',
                        mb: 2,
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha('#00bcd4', 0.1)
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b', mb: 3, lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                    <List dense>
                      {feature.details.map((detail, i) => (
                        <ListItem key={i} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleIcon sx={{ color: '#00bcd4', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={detail}
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: '#2d3748'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Additional Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
            Weitere Funktionen
          </Typography>
          <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 400 }}>
            Noch mehr Features für Ihre tägliche Arbeit
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {additionalFeatures.map((section, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CategoryIcon sx={{ color: '#00bcd4', mr: 1, fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a365d' }}>
                    {section.category}
                  </Typography>
                </Box>
                <List dense>
                  {section.items.map((item, i) => (
                    <ListItem key={i} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <ArrowForwardIcon sx={{ color: '#00bcd4', fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: '#4a5568'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: '#e0f7fa', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
              Ihre Vorteile mit Immo-Automation
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <SpeedIcon sx={{ fontSize: 60, color: '#00bcd4', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                  Zeit sparen
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  Bis zu 15 Stunden pro Woche durch Automatisierung wiederkehrender Aufgaben
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <SecurityIcon sx={{ fontSize: 60, color: '#00bcd4', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                  Sicher & DSGVO-konform
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  Höchste Sicherheitsstandards, deutsche Server, verschlüsselte Datenübertragung
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <CloudDoneIcon sx={{ fontSize: 60, color: '#00bcd4', mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                  Überall verfügbar
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  Cloud-basiert – keine Installation nötig. Zugriff von PC, Tablet und Smartphone
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f2942 0%, #1a365d 100%)',
          color: 'white',
          py: { xs: 10, md: 14 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 3 }}>
            Überzeugt?
          </Typography>
          <Typography variant="h5" sx={{ mb: 5, fontWeight: 400 }}>
            Starten Sie jetzt mit der 14-tägigen kostenlosen Testphase
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{ 
                bgcolor: '#00bcd4',
                color: 'white',
                px: 6,
                py: 2.5,
                fontSize: '1.2rem',
                borderRadius: 8,
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: '#00acc1'
                }
              }}
            >
              Kostenlos testen
            </Button>
            <Button 
              variant="outlined"
              size="large"
              onClick={() => navigate('/preise')}
              sx={{ 
                borderColor: 'white',
                color: 'white',
                px: 6,
                py: 2.5,
                fontSize: '1.2rem',
                borderRadius: 8,
                fontWeight: 700,
                textTransform: 'none',
                borderWidth: 2,
                '&:hover': {
                  borderColor: '#00bcd4',
                  bgcolor: alpha('#00bcd4', 0.15),
                  borderWidth: 2
                }
              }}
            >
              Preise ansehen
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        sx={{ 
          bgcolor: '#f8fafc',
          borderTop: '2px solid #e2e8f0',
          py: 6
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                Immo-Automation
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Moderne Immobilienverwaltung für Vermieter, Hausverwaltungen und Investoren.
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                Produkt
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/features')}>Funktionen</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/preise')}>Preise</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                Rechtliches
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/impressum')}>Impressum</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/datenschutz')}>Datenschutz</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/agb')}>AGB</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                Kontakt
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/kontakt')}>
                Kontaktformular
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
            © 2025 Immo-Automation. Alle Rechte vorbehalten.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default FeaturesPage;
